import React, { useState, useEffect } from 'react';
import { authService } from '../../services/authService';
import { LoginForm } from './LoginForm';
import { SignUpForm } from './SignUpForm';
import { ConfirmationForm } from './ConfirmationForm';

type AuthView = 'login' | 'signup' | 'confirmation';

interface AuthWrapperProps {
  children: React.ReactNode;
  onAuthenticated: () => void;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children, onAuthenticated }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [pendingEmail, setPendingEmail] = useState<string>('');

  useEffect(() => {
    // Check if user is already authenticated
    if (authService.isAuthenticated()) {
      setIsAuthenticated(true);
      onAuthenticated();
    }
  }, [onAuthenticated]);

  // Listen for logout events
  useEffect(() => {
    const checkAuthStatus = () => {
      const currentlyAuthenticated = authService.isAuthenticated();
      if (!currentlyAuthenticated && isAuthenticated) {
        setIsAuthenticated(false);
        setCurrentView('login');
      } else if (currentlyAuthenticated && !isAuthenticated) {
        setIsAuthenticated(true);
        onAuthenticated();
      }
    };

    // Check auth status periodically or on focus
    const interval = setInterval(checkAuthStatus, 1000);
    window.addEventListener('focus', checkAuthStatus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkAuthStatus);
    };
  }, [isAuthenticated, onAuthenticated]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    onAuthenticated();
  };

  const handleSignUpSuccess = (email: string) => {
    setPendingEmail(email);
    setCurrentView('confirmation');
  };

  const handleConfirmationSuccess = () => {
    setCurrentView('login');
    setPendingEmail('');
  };

  const handleShowSignup = () => {
    setCurrentView('signup');
  };

  const handleShowLogin = () => {
    setCurrentView('login');
  };

  const handleBackToSignup = () => {
    setCurrentView('signup');
  };

  if (isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="auth-wrapper">
      {currentView === 'login' && (
        <LoginForm
          onSuccess={handleLoginSuccess}
          onShowSignup={handleShowSignup}
        />
      )}
      
      {currentView === 'signup' && (
        <SignUpForm
          onSuccess={handleSignUpSuccess}
          onShowLogin={handleShowLogin}
        />
      )}
      
      {currentView === 'confirmation' && (
        <ConfirmationForm
          email={pendingEmail}
          onSuccess={handleConfirmationSuccess}
          onBackToSignup={handleBackToSignup}
        />
      )}
    </div>
  );
};